import React, { useState, useEffect } from 'react';
import { Box, SelectChangeEvent } from '@mui/material';
import {
  UserOwnedNftCardType,
  NftCardType,
  NftsWithUserOwnedType,
  DeckType
} from '../../../types/types';
import { DeckDisplay } from './components/DeckDisplay';
import { CreateAndEditDeck } from './components/CreateAndEditDeck';
import { ShowNft } from './components/ShowNft';
import { useNetwork } from '../../../utils/network';
import { track } from '../../../utils/track';

// type NFTCollectionProps = {};

export const NFTCollection = () => {
  const { get } = useNetwork();
  const maxSameCardInDeck = 3;
  const maxSameTokenCardInDeck = 8;
  const numberOfCardsNeeded = 45;
  // The arena has 3 deck slots and shows only the first 3, so a user may keep at
  // most 3 decks (both servers enforce this on save too).
  const maxNumberOfDecks = 3;
  // Portal deck management (create AND edit) is disabled — the builder here has
  // issues; players build/edit their decks in the game itself (shared storage,
  // so it's the same deck). This hides the Create Deck / Current Decks bar and
  // the My Decks / Edit Deck view. Flip to true to re-enable once it's fixed.
  const deckManagementEnabled = false;

  const [currentPage, setCurrentPage] = useState<number>(1);
  const imagesPerPage = 48;

  const [allNftsFromServer, setAllNftsFromServer] = useState<NftCardType[]>([]);
  const [listOfAllUserOwnedNft, setListOfAllUserOwnedNft] = useState<
    UserOwnedNftCardType[]
  >([]);

  const [listOfAllUserDecks, setListOfAllUserDecks] = useState<DeckType[]>([]);
  // Average active-listing price (INC) per card id, for the collection total value.
  const [avgPrices, setAvgPrices] = useState<Record<number, number>>({});
  // Current INC->USD rate (USD per 1 INC) for the collection's $ estimate.
  const [incUsd, setIncUsd] = useState<number>(0);

  const handleWithdrawalComplete = (nftId: number, amount: number) => {
    setListOfAllUserOwnedNft((currentNfts) =>
      currentNfts
        .map((ownedNft) =>
          ownedNft.nft?.id === nftId
            ? {
                ...ownedNft,
                amount: Math.max(0, Number(ownedNft.amount) - amount)
              }
            : ownedNft
        )
        .filter((ownedNft) => Number(ownedNft.amount) > 0)
    );
  };

  const handleDepositComplete = (nftId: number, amount: number) => {
    const depositedNft = allNftsFromServer.find((nft) => nft.id === nftId);
    if (!depositedNft) return;

    setListOfAllUserOwnedNft((currentNfts) => {
      const existingNft = currentNfts.find(
        (ownedNft) => ownedNft.nft?.id === nftId
      );

      if (!existingNft) {
        return [...currentNfts, { nft: depositedNft, amount }];
      }

      return currentNfts.map((ownedNft) =>
        ownedNft.nft?.id === nftId
          ? { ...ownedNft, amount: Number(ownedNft.amount) + amount }
          : ownedNft
      );
    });
  };

  // Re-read what the member owns — after a deposit or withdrawal that was still
  // confirming, so the count catches up once the server finishes it.
  const refreshOwnedNfts = async () => {
    try {
      const response: UserOwnedNftCardType[] = await get('/nft/user', true);
      setListOfAllUserOwnedNft(response);
    } catch (error: any) {
      console.log('error');
    }
  };

  useEffect(() => {
    // get list of all NFTs
    const getListOfAllNfts = async () => {
      try {
        const response: NftCardType[] = await get('/nft/unique', true);
        setAllNftsFromServer(response);
      } catch (error: any) {
        console.log('error');
      }
    };

    // Get user owned Decks
    const getUserDecks = async () => {
      try {
        const response = await get('/nft/getdecks', true);
        setListOfAllUserDecks(response);
      } catch (error: any) {
        console.log('error');
      }
    };

    const getAvgPrices = async () => {
      try {
        const response = await get('/marketplace/average-prices', true);
        setAvgPrices(response?.prices || {});
        setIncUsd(Number(response?.incUsd) || 0);
      } catch (error: any) {
        console.log('error');
      }
    };

    getListOfAllNfts();
    // Get user owned NFTs
    refreshOwnedNfts();
    getUserDecks();
    getAvgPrices();
  }, []);

  const ListOfAllNftWithUserAmountOwned: NftsWithUserOwnedType[] =
    allNftsFromServer.map((cardFromAllCardsList) => {
      const seeIfUserOwnsThatNft = listOfAllUserOwnedNft.filter(
        (nft) => nft.nft?.id === cardFromAllCardsList.id
      );

      return {
        nft: cardFromAllCardsList,
        owned:
          seeIfUserOwnsThatNft.length > 0 ? seeIfUserOwnsThatNft[0].amount : 0,
        // copies from a locked free starter pack (cannot be listed or withdrawn yet)
        locked:
          seeIfUserOwnsThatNft.length > 0 ? seeIfUserOwnsThatNft[0].locked || 0 : 0
      };
    });

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [rarityFilters, setRarityFilters] = useState<string[]>([]);
  const [typeFilters, setTypeFilters] = useState<string[]>([]);
  const [factionFilters, setFactionFilters] = useState<string[]>([]);
  const [attackFromFilter, setAttackFromFilter] = useState<number | null>(null);
  const [attackToFilter, setAttackToFilter] = useState<number | null>(null);
  const [defenceFromFilter, setDefenceFromFilter] = useState<number | null>(
    null
  );
  const [defenceToFilter, setDefenceToFilter] = useState<number | null>(null);
  const [showOwnedCards, setShowOwnedCards] = useState<boolean>(false);
  const [showUnownedCards, setShowUnownedCards] = useState<boolean>(false);
  const [createEditDeck, setCreateEditDeck] = useState<DeckType | null>(null);
  const [isCreateEditDeckModeEnabled, setIsCreateEditDeckModeEnabled] =
    useState<boolean>(false);
  // const [deckCost, setDeckCost] = useState<number>(0);
  const [showDiscardDeckModalWarning, setShowDiscardDeckModalWarning] =
    useState<boolean>(false);
  const [showAllUserDecks, setShowAllUserDecks] = useState<boolean>(false);
  const [isEditOrCreate, setIsEditOrCreate] = useState<'edit' | 'create'>(
    'create'
  );

  const handleRarityChange = (
    event: SelectChangeEvent<typeof rarityFilters>
  ) => {
    const {
      target: { value }
    } = event;
    const next = typeof value === 'string' ? value.split(',') : value;
    setRarityFilters(next);
    track('collection.filter', { kind: 'rarity', value: next });
  };

  const handleTypeChange = (event: SelectChangeEvent<typeof typeFilters>) => {
    const {
      target: { value }
    } = event;
    const next = typeof value === 'string' ? value.split(',') : value;
    setTypeFilters(next);
    track('collection.filter', { kind: 'type', value: next });
  };

  const handleFactionChange = (
    event: SelectChangeEvent<typeof factionFilters>
  ) => {
    const {
      target: { value }
    } = event;
    const next = typeof value === 'string' ? value.split(',') : value;
    setFactionFilters(next);
    track('collection.filter', { kind: 'faction', value: next });
  };

  const [showAdvancedFilters, setShowAdvancedFilters] =
    useState<boolean>(false);

  const filteredCards = () => {
    const filterCardsByName = ListOfAllNftWithUserAmountOwned.filter((card) => {
      if (!!searchQuery) {
        return card.nft.name.toLowerCase().includes(searchQuery.toLowerCase());
      } else {
        return card;
      }
    });

    const filterCardsByType = filterCardsByName.filter((card) => {
      if (typeFilters.length > 0) {
        return typeFilters.includes(card.nft.type);
      } else {
        return card;
      }
    });

    const filterCardsByFaction = filterCardsByType.filter((card) => {
      if (factionFilters.length > 0) {
        return factionFilters.includes(card.nft.faction);
      } else {
        return card;
      }
    });

    const filterCardsByRarity = filterCardsByFaction.filter((card) => {
      if (rarityFilters.length > 0) {
        return rarityFilters.includes(card.nft.rarity);
      } else {
        return card;
      }
    });

    const filterCardsByAttackFrom = filterCardsByRarity.filter((card) => {
      if (attackFromFilter !== null) {
        return (
          (card.nft.type === 'character' || card.nft.type === 'influencer') &&
          card.nft.attack >= attackFromFilter
        );
      } else {
        return card;
      }
    });

    const filterCardsByAttackTo = filterCardsByAttackFrom.filter((card) => {
      if (attackToFilter !== null) {
        return (
          (card.nft.type === 'character' || card.nft.type === 'influencer') &&
          card.nft.attack <= attackToFilter
        );
      } else {
        return card;
      }
    });

    const filterCardsByDefenceFrom = filterCardsByAttackTo.filter((card) => {
      if (defenceFromFilter !== null) {
        return (
          (card.nft.type === 'character' || card.nft.type === 'influencer') &&
          card.nft.defence >= defenceFromFilter
        );
      } else {
        return card;
      }
    });

    const filterCardsByDefenceTo = filterCardsByDefenceFrom.filter((card) => {
      if (defenceToFilter !== null) {
        return (
          (card.nft.type === 'character' || card.nft.type === 'influencer') &&
          card.nft.defence <= defenceToFilter
        );
      } else {
        return card;
      }
    });

    const filterCardsByOwned = filterCardsByDefenceTo.filter((card) => {
      if (showOwnedCards) {
        return card.owned > 0;
      } else {
        return card;
      }
    });

    const filterCardsByUnowned = filterCardsByOwned.filter((card) => {
      if (showUnownedCards) {
        return card.owned === 0;
      } else {
        return card;
      }
    });

    return Array.from(new Set([...filterCardsByUnowned]));
  };

  const resetFilters = () => {
    setSearchQuery('');
    setRarityFilters([]);
    setTypeFilters([]);
    setFactionFilters([]);
    setAttackFromFilter(null);
    setAttackToFilter(null);
    setDefenceFromFilter(null);
    setDefenceToFilter(null);
    setShowUnownedCards(false);

    if (!isCreateEditDeckModeEnabled) {
      setShowOwnedCards(false);
    }
    track('collection.filter_reset');
  };

  const createNewDeck = () => {
    // Portal deck management is disabled (see deckManagementEnabled); also never
    // enter create mode past the 3-deck cap the servers enforce.
    if (!deckManagementEnabled) return;
    if (listOfAllUserDecks.length >= maxNumberOfDecks) return;
    setIsEditOrCreate('create');
    setIsCreateEditDeckModeEnabled(true);
    setShowOwnedCards(true);
    setCreateEditDeck({
      deckId: null,
      deckName: `Custom Deck ${listOfAllUserDecks.length + 1}`,
      deck: []
    });
    // setDeckCost(0);
  };

  const loadDeck = (deck: DeckType) => {
    // Portal deck editing is disabled (see deckManagementEnabled).
    if (!deckManagementEnabled) return;
    setIsEditOrCreate('edit');
    setShowAllUserDecks(false);
    setIsCreateEditDeckModeEnabled(true);
    setShowOwnedCards(true);
    setCreateEditDeck(deck);
    // let calculatedDeckCost = 0;
    // deck.deck.map((listOfCards) => {
    //   const { card, quantity } = listOfCards;

    //   calculatedDeckCost = calculatedDeckCost + card.cost * quantity;
    // });

    // setDeckCost(calculatedDeckCost);
  };

  // Calculate the start and end indices of the images for the current page
  const startIndex = (currentPage - 1) * imagesPerPage;
  const endIndex = startIndex + imagesPerPage;
  const paginatedImages = filteredCards().slice(startIndex, endIndex);

  const totalPages = Math.ceil(filteredCards().length / imagesPerPage);

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prevPage) => prevPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prevPage) => prevPage - 1);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {deckManagementEnabled && isCreateEditDeckModeEnabled && !showAllUserDecks && (
        <CreateAndEditDeck
          // deckCost={deckCost}
          // setDeckCost={setDeckCost}
          // maxDeckCost={maxDeckCost}
          numberOfCardsNeeded={numberOfCardsNeeded}
          createEditDeck={createEditDeck}
          setCreateEditDeck={setCreateEditDeck}
          setIsCreateEditDeckModeEnabled={setIsCreateEditDeckModeEnabled}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          showAdvancedFilters={showAdvancedFilters}
          setShowAdvancedFilters={setShowAdvancedFilters}
          rarityFilters={rarityFilters}
          handleRarityChange={handleRarityChange}
          typeFilters={typeFilters}
          handleTypeChange={handleTypeChange}
          factionFilters={factionFilters}
          handleFactionChange={handleFactionChange}
          setAttackFromFilter={setAttackFromFilter}
          setAttackToFilter={setAttackToFilter}
          setDefenceFromFilter={setDefenceFromFilter}
          setDefenceToFilter={setDefenceToFilter}
          showOwnedCards={showOwnedCards}
          setShowOwnedCards={setShowOwnedCards}
          showUnownedCards={showUnownedCards}
          setShowUnownedCards={setShowUnownedCards}
          resetFilters={resetFilters}
          paginatedImages={paginatedImages}
          isCreateEditDeckModeEnabled={isCreateEditDeckModeEnabled}
          maxSameCardInDeck={maxSameCardInDeck}
          currentPage={currentPage}
          totalPages={totalPages}
          handlePrevPage={handlePrevPage}
          handleNextPage={handleNextPage}
          maxSameTokenCardInDeck={maxSameTokenCardInDeck}
          isEditOrCreate={isEditOrCreate}
          onWithdrawalComplete={handleWithdrawalComplete}
          onDepositComplete={handleDepositComplete}
        />
      )}

      {deckManagementEnabled && !isCreateEditDeckModeEnabled && showAllUserDecks && (
        <DeckDisplay
          loadDeck={loadDeck}
          setShowAllUserDecks={setShowAllUserDecks}
          userDecks={listOfAllUserDecks}
        />
      )}

      {(!deckManagementEnabled ||
        (!isCreateEditDeckModeEnabled && !showAllUserDecks)) && (
        <ShowNft
          // deckCost={deckCost}
          // setDeckCost={setDeckCost}
          // maxDeckCost={maxDeckCost}
          createEditDeck={createEditDeck}
          setCreateEditDeck={setCreateEditDeck}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          showAdvancedFilters={showAdvancedFilters}
          setShowAdvancedFilters={setShowAdvancedFilters}
          rarityFilters={rarityFilters}
          handleRarityChange={handleRarityChange}
          typeFilters={typeFilters}
          handleTypeChange={handleTypeChange}
          factionFilters={factionFilters}
          handleFactionChange={handleFactionChange}
          setAttackFromFilter={setAttackFromFilter}
          setAttackToFilter={setAttackToFilter}
          setDefenceFromFilter={setDefenceFromFilter}
          setDefenceToFilter={setDefenceToFilter}
          showOwnedCards={showOwnedCards}
          setShowOwnedCards={setShowOwnedCards}
          showUnownedCards={showUnownedCards}
          setShowUnownedCards={setShowUnownedCards}
          resetFilters={resetFilters}
          paginatedImages={paginatedImages}
          isCreateEditDeckModeEnabled={isCreateEditDeckModeEnabled}
          maxSameCardInDeck={maxSameCardInDeck}
          currentPage={currentPage}
          totalPages={totalPages}
          handlePrevPage={handlePrevPage}
          handleNextPage={handleNextPage}
          createNewDeck={createNewDeck}
          deckManagementEnabled={deckManagementEnabled}
          canCreateDeck={listOfAllUserDecks.length < maxNumberOfDecks}
          setShowAllUserDecks={setShowAllUserDecks}
          numberOfCardsNeeded={numberOfCardsNeeded}
          maxSameTokenCardInDeck={maxSameTokenCardInDeck}
          listOfAllUserOwnedNft={listOfAllUserOwnedNft}
          avgPrices={avgPrices}
          incUsd={incUsd}
          onWithdrawalComplete={handleWithdrawalComplete}
          onDepositComplete={handleDepositComplete}
          onCollectionStale={refreshOwnedNfts}
        />
      )}
    </Box>
  );
};
